import { LiveTag } from '../../components/Freshness.tsx';
import {
  FaultLine, ReadTag, faultOf, stateWord, unread, worstRead, type Read,
} from '../../components/states/index.ts';
import { useFeedWord } from '../../shell/TopBar.tsx';
import { duration, utc, utcTime } from '../../components/format.ts';
import { StatusDot } from '../../components/StatusDot.tsx';
import type { Health } from './freshness.ts';
import styles from './FeedTag.module.css';

/* ==========================================================================
   The two things a panel says about its own freshness.

   FeedTag     the chip in the panel header: `Live` or `Live off 17s`.
   StaleLine   one row inside the body, in --warn, naming the endpoint and
               the last good read, when the chip has gone off.

   `LiveTag` (components/Freshness.tsx) renders the dot, the word, the
   frozen-at clock and — since this wrapper asked for it — the AGE of the last
   good read, which is the number an operator reads first ("how long have I
   been looking at a photograph"). All that is left here is turning a `Health`
   into those props.
   ========================================================================== */

export function FeedTag({
  health,
  now,
  /** Only ever true where a stream is genuinely pushing, e.g. live MQTT. */
  pulse = false,
  /** Word for the live state. Defaults to the console's own — see
      `shell/TopBar`'s `useFeedWord`: the transport names it, not the panel. */
  label,
  read,
}: {
  health: Health;
  now: number;
  pulse?: boolean | undefined;
  label?: string | undefined;
  /**
   * THE READ(S) BEHIND THE PANEL, so the tag can refuse to claim a cadence it
   * has no evidence for.
   *
   * `Health.live` answers "is what I am holding current", and it is true by
   * construction before the first read has landed — `lastGoodAt === 0`
   * counted as fine as long as something was being attempted. So a cold start
   * and a whole outage both flew `● Polling`, filled dot, over `Queue (—)` and
   * an empty board. `unread` is the missing premise, and once it is true the
   * console's own per-source tag says the honest word: `Reading 6s` while a
   * request is out, `no reply` once one has failed.
   */
  read: Read | readonly Read[];
}) {
  const feedWord = useFeedWord();
  const { live, ageMs, lastGoodAt, worst } = health;
  /* `diagnose`, because this tag is the panel header's only state word and the
     board's fault chrome (`StaleLine`) only fires once a read has FAILED —
     leaving nothing else on the header to say which endpoint is silent. */
  if (unread(read)) return <ReadTag read={worstRead(read)} diagnose />;
  return (
    <LiveTag
      live={live}
      label={label ?? feedWord}
      pulse={pulse && live}
      {...(live || ageMs === null
        ? {}
        : {
            age: ageMs,
            ageTitle: `Last good read of ${worst.endpoint} — ${utc(new Date(lastGoodAt ?? now).toISOString())}`,
          })}
    />
  );
}

/**
 * The operational sentence.
 *
 * A read that fails is not a robot fault, so it is not `--error` and it does
 * not print the browser's `TypeError: Failed to fetch` as its headline: that
 * string tells an operator nothing about their fleet and everything about
 * our transport. What they need is which endpoint stopped answering, what the
 * rows underneath actually are, and how old they are. The raw failure keeps
 * its place at the end, dim, for whoever is going to chase it.
 */
export function StaleLine({
  health,
  now,
  read,
}: {
  health: Health;
  now: number;
  /**
   * The panel's read. Two things follow from it, and both are corrections.
   *
   * NOTHING ON SCREEN, NOTHING TO SAY. This line's whole job is to date the
   * rows underneath it — "every row below is that read, not now". With no rows
   * under it there is nothing to date, and the panel's own `ReadBody` is
   * already printing a `FaultLine` (endpoint, class, runs lost) and one line
   * at the first row's position. This line then became the third statement of
   * one event, 20px below the second.
   *
   * AND THE FAULT LINE IS THE ONE THAT SHOULD SURVIVE, because this one ended
   * in the browser's own words. `Failed to fetch` tells an operator nothing
   * about their fleet and everything about our transport; the console's word
   * for it is `no reply`, and it is `stateWord`'s, so the same event reads the
   * same in the panel, the banner and the supply ledger.
   */
  read: Read | readonly Read[];
}) {
  const { lastGoodAt, ageMs, worst } = health;
  /* No rows under this line — hand the whole statement to the fault line the
     module already draws for this read, which says the same thing once. */
  if (unread(read)) return <FaultLine read={worstRead(read)} />;

  const when =
    lastGoodAt === null
      ? null
      : `${utcTime(new Date(lastGoodAt).toISOString())}, ${duration(ageMs ?? now - lastGoodAt)} ago`;

  const headline =
    lastGoodAt === null
      ? `${worst.endpoint} has not answered yet — nothing below is a reading.`
      : worst.paused && !worst.failing
        ? `Polling held. ${worst.endpoint} last read ${when} — every row below is that read, not now.`
        : worst.failing
          ? `${worst.endpoint} stopped answering — last good read ${when}. Every row below is that read, not now.`
          : `${worst.endpoint} has missed its ${Math.round(worst.intervalMs / 1000)}s cadence — last good read ${when}. Every row below is that read, not now.`;

  return (
    <div className={styles.stale} role="status">
      <StatusDot tone="warn" hollow />
      <span className={styles.staleText}>{headline}</span>
      {/* THE CONSOLE'S WORD, NOT THE BROWSER'S. `Failed to fetch` is 22
          characters of transport detail on the one line an operator reads to
          decide whether to trust the rows below it. The verbatim string is not
          lost — it is on the supply ledger at the foot of the surface, in the
          column built for it. */}
      {worst.reason !== null && (
        <span className={styles.staleReason}>
          {stateWord(faultOf(worst.reason)?.class ?? 'unknown')}
        </span>
      )}
    </div>
  );
}
